import { Link, useNavigate } from '@tanstack/react-router';
import { AxiosError } from 'axios';
import {
  CircleArrowLeft,
  HelpCircle,
  Loader2,
  TriangleAlert,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { queryClient } from '~app/query-client';

import { useConfirmStripeConnectMigrationMutation } from '~entities/stripe/api/confirm-migration';
import {
  type StripeAccountMismatchError,
  useCreateLegacyStripeCredentialsMutation,
} from '~entities/stripe/api/create-legacy-credentials';
import { useDeleteStripeCredentialsMutation } from '~entities/stripe/api/delete-credentials';
import { useGetStripeCredentials } from '~entities/stripe/api/fetch-credentials';
import {
  getSquareConnectUrl,
  useDisconnectSquareConnectMutation,
} from '~entities/stripe/api/square-connect';
import { getStripeConnectUrl } from '~entities/stripe/api/stripe-connect';
import { useGetStudioDefaultPaymentProvider } from '~entities/studios/api/get-studio-default-payment-provider';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';

import PaymentProviderCard from '~features/dashboard/ui/payment-provider-card';

import { KEYS } from '~shared/api/query-keys';
import { cn } from '~shared/shadcn/lib/utils';
import { Alert, AlertDescription, AlertTitle } from '~shared/shadcn/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import { getErrorMessage } from '~shared/utils/error-handler';

interface MismatchState {
  token: string;
  connected: string;
  legacy: string;
  outcome: 'mismatch' | 'unverified';
}

const readStripeConnectQuery = () => {
  const params = new URLSearchParams(window.location.search);
  const status = params.get('stripeConnect');
  if (!status) return null;
  return {
    status,
    message: params.get('message') ?? '',
    token: params.get('token') ?? '',
    connected: params.get('connected') ?? '',
    legacy: params.get('legacy') ?? '',
    outcome: (params.get('outcome') as MismatchState['outcome']) ?? 'mismatch',
  };
};

const readSquareConnectQuery = () => {
  const params = new URLSearchParams(window.location.search);
  const status = params.get('squareConnect');
  if (!status) return null;
  return {
    status,
    message: params.get('message') ?? '',
  };
};

/** Strict Mode remounts the page; consume each OAuth callback URL only once. */
let handledOauthSearch: string | null = null;
const consumeOauthQueryOnce = <T,>(read: () => T | null): T | null => {
  const search = window.location.search;
  if (!search || search === handledOauthSearch) return null;
  const query = read();
  if (!query) return null;
  handledOauthSearch = search;
  return query;
};

export const PaymentIntegrations = () => {
  const navigate = useNavigate({ from: '/payment/payment-integration' });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isStripeConnectLoading, setIsStripeConnectLoading] = useState(false);
  const [isSquareConnectLoading, setIsSquareConnectLoading] = useState(false);
  const [isSquareDeleteDialogOpen, setIsSquareDeleteDialogOpen] =
    useState(false);
  const [mismatch, setMismatch] = useState<MismatchState | null>(null);
  const [isLegacyDialogOpen, setIsLegacyDialogOpen] = useState(false);
  const [legacyForm, setLegacyForm] = useState({
    publicKey: '',
    secretKey: '',
  });
  const [legacyMismatch, setLegacyMismatch] =
    useState<StripeAccountMismatchError | null>(null);

  const deleteMutation = useDeleteStripeCredentialsMutation();
  const disconnectSquareMutation = useDisconnectSquareConnectMutation();
  const confirmMigrationMutation = useConfirmStripeConnectMigrationMutation();
  const createLegacyMutation = useCreateLegacyStripeCredentialsMutation();

  const { data: studioDetails } = useGetStudioDetails();
  const { data: stripeIntegrationStatus } = useGetStripeCredentials(
    studioDetails?._id ?? '',
  );
  const { data: studioPaymentProvider } = useGetStudioDefaultPaymentProvider();

  const hasLegacyCredentials = stripeIntegrationStatus?.hasLegacyKeys ?? false;
  const hasStripeConnect = stripeIntegrationStatus?.hasStripeConnect ?? false;
  const hasSquareConnect = stripeIntegrationStatus?.hasSquareConnect ?? false;
  const isSquareProvider = studioPaymentProvider?.paymentProvider === 'square';
  const hasStripeIntegration = hasLegacyCredentials || hasStripeConnect;
  const hasPaymentProviderIntegration =
    hasStripeIntegration || hasSquareConnect;

  const clearStripeConnectQuery = () => {
    void navigate({
      to: '/payment/payment-integration',
      search: {},
      replace: true,
    });
  };

  useEffect(() => {
    const query = consumeOauthQueryOnce(readStripeConnectQuery);
    if (!query) return;

    if (query.status === 'success') {
      toast.success('Stripe connected successfully');
    } else if (query.status === 'error') {
      toast.error(query.message || 'Failed to connect Stripe account');
    } else if (query.status === 'mismatch') {
      if (query.token) {
        setMismatch({
          token: query.token,
          connected: query.connected,
          legacy: query.legacy,
          outcome: query.outcome,
        });
      } else {
        toast.error(
          'Stripe account mismatch confirmation is missing or invalid. Please try connecting again.',
        );
      }
    }

    // Defer clear so React 18 StrictMode double-mount still reads OAuth params.
    setTimeout(() => clearStripeConnectQuery(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount for OAuth redirect params
  }, []);

  useEffect(() => {
    const query = consumeOauthQueryOnce(readSquareConnectQuery);
    if (!query) return;

    if (query.status === 'success') {
      toast.success('Square connected successfully');
      void queryClient.invalidateQueries({
        queryKey: [KEYS.STRIPE, KEYS.CREDENTIALS],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.STUDIO_DEFAULT_PAYMENT_PROVIDER],
      });
    } else if (query.status === 'error') {
      toast.error(query.message || 'Failed to connect Square account');
    }

    setTimeout(() => clearStripeConnectQuery(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount for OAuth redirect params
  }, []);

  const stripeStatusBadges = useMemo(() => {
    if (hasLegacyCredentials) {
      return [
        {
          label: 'Legacy API Keys',
          className: 'bg-amber-500 hover:bg-amber-600',
        },
      ];
    }

    if (hasStripeConnect) {
      return [
        {
          label: 'Stripe Connect',
          className: 'bg-green-500 hover:bg-green-600',
        },
      ];
    }

    return [];
  }, [hasLegacyCredentials, hasStripeConnect]);

  const deleteDialogDescription = hasStripeConnect
    ? 'This disconnects Stripe Connect from Enrollio for this studio. Payments and checkout will stop working until you connect Stripe again.'
    : 'This removes your stored Stripe API keys from Enrollio. To accept payments again, you will need to connect with Stripe Connect.';

  const handleStripeConnect = async () => {
    setIsStripeConnectLoading(true);
    try {
      const { url } = await getStripeConnectUrl({
        studioId: studioDetails?._id,
        locationId: studioDetails?.locationId,
      });
      window.open(url, '_blank', 'noopener,noreferrer');
      setIsStripeConnectLoading(false);
    } catch (error) {
      console.error('Stripe Connect error:', error);
      toast.error('Failed to connect to Stripe');
      setIsStripeConnectLoading(false);
    }
  };

  const handleSquareConnect = async () => {
    setIsSquareConnectLoading(true);
    try {
      const { url } = await getSquareConnectUrl({
        studioId: studioDetails?._id,
        locationId: studioDetails?.locationId,
      });
      window.open(url, '_blank', 'noopener,noreferrer');
      setIsSquareConnectLoading(false);
    } catch (error) {
      console.error('Square Connect error:', error);
      toast.error(getErrorMessage(error, 'Failed to connect to Square'));
      setIsSquareConnectLoading(false);
    }
  };

  const runDeleteIntegration = async () => {
    if (!studioDetails?._id) return;

    try {
      await deleteMutation.mutateAsync(studioDetails._id);
      setIsDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting Stripe integration:', error);
      toast.error('Failed to remove Stripe integration');
    }
  };

  const runDeleteSquareIntegration = async () => {
    try {
      await disconnectSquareMutation.mutateAsync();
      setIsSquareDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting Square integration:', error);
    }
  };

  const runConfirmMigration = async () => {
    if (!mismatch) return;
    try {
      await confirmMigrationMutation.mutateAsync(mismatch.token);
      setMismatch(null);
    } catch {
      // toast handled in the mutation's onError
    }
  };

  const submitLegacyKeys = async (force: boolean) => {
    if (!studioDetails?._id) return;
    setLegacyMismatch(null);
    try {
      await createLegacyMutation.mutateAsync({
        publicKey: legacyForm.publicKey.trim(),
        secretKey: legacyForm.secretKey.trim(),
        studioId: studioDetails._id,
        force,
      });
      setIsLegacyDialogOpen(false);
      setLegacyForm({ publicKey: '', secretKey: '' });
    } catch (error) {
      // The backend wraps HttpException bodies under `error` (custom-exception.filter).
      const apiError =
        error instanceof AxiosError
          ? (
              error as AxiosError<{
                error?: StripeAccountMismatchError | string;
              }>
            ).response?.data?.error
          : undefined;

      if (
        apiError &&
        typeof apiError === 'object' &&
        apiError.code === 'account_mismatch'
      ) {
        setLegacyMismatch(apiError);
      } else {
        const message =
          typeof apiError === 'string'
            ? apiError
            : (apiError?.message ??
              (error instanceof Error
                ? error.message
                : 'Failed to save API keys'));
        toast.error(message);
      }
    }
  };

  // Legacy studios can still migrate to Connect; only block while loading,
  // when already on Connect, when the studio is still on Square (including
  // after a Square disconnect that leaves paymentProvider as square), or
  // before studio details are available.
  const stripeConnectDisabled =
    isStripeConnectLoading ||
    hasStripeConnect ||
    hasSquareConnect ||
    isSquareProvider ||
    !studioDetails;

  // Leftover Stripe flags after a Stripe-to-Square migration must not block
  // Square reconnect. Stripe Remove is hidden while paymentProvider is square.
  const squareConnectDisabled =
    isSquareConnectLoading ||
    hasSquareConnect ||
    (hasStripeIntegration && !isSquareProvider) ||
    !studioDetails;

  const squarePrimaryLabel = isSquareConnectLoading ? (
    <span className='flex items-center justify-center gap-2'>
      <Loader2 className='size-4 animate-spin' />
      Connecting...
    </span>
  ) : hasSquareConnect ? (
    'Connected via Square'
  ) : hasStripeIntegration && !isSquareProvider ? (
    'Contact Support to Switch'
  ) : (
    'Connect Square'
  );

  const connectPrimaryLabel = isStripeConnectLoading ? (
    <span className='flex items-center justify-center gap-2'>
      <Loader2 className='size-4 animate-spin' />
      Connecting...
    </span>
  ) : isSquareProvider || hasSquareConnect ? (
    'Contact Support to Switch'
  ) : hasStripeConnect ? (
    'Connected via Stripe Connect'
  ) : hasLegacyCredentials ? (
    'Migrate to Stripe Connect'
  ) : (
    'Stripe Connect'
  );

  return (
    <div>
      <Link to='/dashboard' className='flex min-w-0 w-fit items-center gap-2'>
        <CircleArrowLeft className='size-6 shrink-0' />
        <h2 className='min-w-0 text-xl font-semibold sm:text-2xl'>
          Payment Integrations
        </h2>
      </Link>
      <div className='mt-8 border-t' />
      <div className='container mx-auto min-w-0 max-w-7xl px-0 py-6 sm:px-6 sm:py-12'>
        <section className='mb-8 text-center sm:mb-16'>
          <h1 className='mb-4 text-2xl font-bold sm:text-5xl'>
            Payment Integrations
          </h1>
          <p className='mx-auto max-w-2xl text-base text-muted-foreground sm:text-xl'>
            Connect your preferred payment provider to start processing
            payments.
          </p>
        </section>

        {hasPaymentProviderIntegration ? (
          <Alert className='mb-8 border-amber-300 bg-amber-50 text-amber-950 [&>svg]:text-amber-800'>
            <TriangleAlert className='size-4' />
            <AlertTitle>Payment provider migrations require support</AlertTitle>
            <AlertDescription className='text-amber-900'>
              Do not disconnect your current provider or connect a different one
              to migrate between Stripe and Square. Saved payment methods and
              active billing do not transfer automatically.{' '}
              <a
                href='mailto:support@enrollio.ai'
                className='font-semibold underline underline-offset-2'
              >
                Contact Enrollio Support
              </a>{' '}
              before changing payment providers.
            </AlertDescription>
          </Alert>
        ) : null}

        <section className='mb-16'>
          <div className='grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3'>
            <PaymentProviderCard
              name='Stripe'
              logo='/stripe-4.svg'
              isActive={hasStripeIntegration && !isSquareProvider}
              isComingSoon={false}
              statusBadges={isSquareProvider ? [] : stripeStatusBadges}
              primaryAction={{
                label: connectPrimaryLabel,
                onClick: () => {
                  void handleStripeConnect();
                },
                className:
                  'border border-yellow-500 bg-yellow-400 text-yellow-950 shadow hover:bg-yellow-500',
                disabled: stripeConnectDisabled,
              }}
              secondaryAction={
                hasStripeIntegration && !isSquareProvider
                  ? {
                      label: 'Remove Integration',
                      onClick: () => {
                        setIsDeleteDialogOpen(true);
                      },
                      variant: 'outline',
                      className: 'text-destructive hover:text-destructive',
                    }
                  : undefined
              }
              tertiaryAction={
                studioDetails && !hasLegacyCredentials && !isSquareProvider
                  ? {
                      label: hasStripeConnect
                        ? 'Switch to legacy API keys'
                        : 'Use legacy API keys',
                      onClick: () => {
                        setLegacyMismatch(null);
                        setIsLegacyDialogOpen(true);
                      },
                      variant: 'link',
                      className: 'text-muted-foreground hover:text-foreground',
                    }
                  : undefined
              }
            />
            <PaymentProviderCard
              name='Authorize.net'
              logo='/authorize-net-logo.png'
              isActive={false}
              isComingSoon={true}
              onAction={() => null}
            />
            <PaymentProviderCard
              name='Square'
              logo='/square_payment gateway_icon.png'
              isActive={hasSquareConnect}
              isComingSoon={false}
              statusBadges={
                hasSquareConnect
                  ? [
                      {
                        label: 'Square Connect',
                        className: 'bg-green-500 hover:bg-green-600',
                      },
                    ]
                  : []
              }
              primaryAction={{
                label: squarePrimaryLabel,
                onClick: () => {
                  void handleSquareConnect();
                },
                className:
                  'border border-yellow-500 bg-yellow-400 text-yellow-950 shadow hover:bg-yellow-500',
                disabled: squareConnectDisabled,
              }}
              secondaryAction={
                hasSquareConnect
                  ? {
                      label: 'Remove Integration',
                      onClick: () => {
                        setIsSquareDeleteDialogOpen(true);
                      },
                      variant: 'outline',
                      className: 'text-destructive hover:text-destructive',
                    }
                  : undefined
              }
            />
          </div>
        </section>

        <AlertDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove Stripe integration?</AlertDialogTitle>
              <AlertDialogDescription>
                {deleteDialogDescription}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteMutation.isPending}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                className={cn(
                  'bg-destructive text-destructive-foreground hover:bg-destructive/90',
                  deleteMutation.isPending && 'pointer-events-none opacity-70',
                )}
                onClick={(e) => {
                  e.preventDefault();
                  void runDeleteIntegration();
                }}
              >
                {deleteMutation.isPending ? (
                  <>
                    <Loader2 className='mr-2 inline size-4 animate-spin' />
                    Removing…
                  </>
                ) : (
                  'Remove Integration'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog
          open={isSquareDeleteDialogOpen}
          onOpenChange={setIsSquareDeleteDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove Square integration?</AlertDialogTitle>
              <AlertDialogDescription>
                This disconnects Square from Enrollio for this studio. Payments
                will stop working until you connect Square again. This does not
                switch the studio back to Stripe.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={disconnectSquareMutation.isPending}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                className={cn(
                  'bg-destructive text-destructive-foreground hover:bg-destructive/90',
                  disconnectSquareMutation.isPending &&
                    'pointer-events-none opacity-70',
                )}
                onClick={(e) => {
                  e.preventDefault();
                  void runDeleteSquareIntegration();
                }}
              >
                {disconnectSquareMutation.isPending ? (
                  <>
                    <Loader2 className='mr-2 inline size-4 animate-spin' />
                    Removing…
                  </>
                ) : (
                  'Remove Integration'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog
          open={!!mismatch}
          onOpenChange={(open) => {
            if (!open) setMismatch(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className='flex items-center gap-2'>
                <TriangleAlert className='size-5 text-amber-500' />
                Stripe account does not match
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className='space-y-3'>
                  <p>
                    {mismatch?.outcome === 'unverified'
                      ? 'We could not verify that the account you just connected matches your existing Stripe API keys.'
                      : 'The Stripe account you just connected is different from the account tied to your existing API keys.'}
                  </p>
                  <div className='rounded-md bg-muted p-3 text-sm'>
                    <div>
                      <span className='font-medium'>Connected account:</span>{' '}
                      {mismatch?.connected || 'unknown'}
                    </div>
                    <div>
                      <span className='font-medium'>Existing key account:</span>{' '}
                      {mismatch?.legacy || 'unknown'}
                    </div>
                  </div>
                  <p>
                    Continuing will replace your current keys and route all
                    payments to the connected account. Your old API keys will be
                    archived.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={confirmMigrationMutation.isPending}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                className={cn(
                  'bg-amber-500 text-white hover:bg-amber-600',
                  confirmMigrationMutation.isPending &&
                    'pointer-events-none opacity-70',
                )}
                onClick={(e) => {
                  e.preventDefault();
                  void runConfirmMigration();
                }}
              >
                {confirmMigrationMutation.isPending ? (
                  <>
                    <Loader2 className='mr-2 inline size-4 animate-spin' />
                    Migrating…
                  </>
                ) : (
                  'Migrate anyway'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Dialog
          open={isLegacyDialogOpen}
          onOpenChange={(open) => {
            setIsLegacyDialogOpen(open);
            if (!open) {
              setLegacyMismatch(null);
              setLegacyForm({ publicKey: '', secretKey: '' });
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Switch to legacy API keys</DialogTitle>
              <DialogDescription>
                Using direct API keys is not recommended. If your studio is
                currently on Stripe Connect, the keys must belong to the same
                Stripe account.
              </DialogDescription>
            </DialogHeader>
            <div className='space-y-4'>
              <div className='space-y-2'>
                <Label htmlFor='stripe-public-key'>Publishable key</Label>
                <Input
                  id='stripe-public-key'
                  placeholder='pk_live_...'
                  value={legacyForm.publicKey}
                  onChange={(e) => {
                    setLegacyMismatch(null);
                    setLegacyForm((prev) => ({
                      ...prev,
                      publicKey: e.target.value,
                    }));
                  }}
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='stripe-secret-key'>Secret key</Label>
                <Input
                  id='stripe-secret-key'
                  type='password'
                  autoComplete='new-password'
                  placeholder='sk_live_...'
                  value={legacyForm.secretKey}
                  onChange={(e) => {
                    setLegacyMismatch(null);
                    setLegacyForm((prev) => ({
                      ...prev,
                      secretKey: e.target.value,
                    }));
                  }}
                />
              </div>
              {legacyMismatch ? (
                <div className='rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900'>
                  <div className='flex items-center gap-2 font-medium'>
                    <TriangleAlert className='size-4' />
                    {legacyMismatch.message}
                  </div>
                  <div className='mt-2'>
                    <div>
                      <span className='font-medium'>Connect account:</span>{' '}
                      {legacyMismatch.connectAccountId || 'unknown'}
                    </div>
                    <div>
                      <span className='font-medium'>Key account:</span>{' '}
                      {legacyMismatch.legacyAccountId || 'unknown'}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
            <DialogFooter>
              {legacyMismatch ? (
                <Button
                  variant='destructive'
                  disabled={createLegacyMutation.isPending}
                  onClick={() => void submitLegacyKeys(true)}
                >
                  {createLegacyMutation.isPending ? (
                    <>
                      <Loader2 className='mr-2 inline size-4 animate-spin' />
                      Saving…
                    </>
                  ) : (
                    'Save anyway'
                  )}
                </Button>
              ) : (
                <Button
                  disabled={
                    createLegacyMutation.isPending ||
                    !legacyForm.publicKey.trim() ||
                    !legacyForm.secretKey.trim()
                  }
                  onClick={() => void submitLegacyKeys(false)}
                >
                  {createLegacyMutation.isPending ? (
                    <>
                      <Loader2 className='mr-2 inline size-4 animate-spin' />
                      Saving…
                    </>
                  ) : (
                    'Save keys'
                  )}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className='fixed bottom-8 right-8'>
          <Button
            variant='outline'
            size='icon'
            className='size-16 rounded-full border-0 bg-background/80 shadow-lg backdrop-blur-sm transition-all hover:shadow-xl'
            onClick={() => window.open('/docs/payment-integration', '_blank')}
          >
            <HelpCircle className='size-7' />
            <span className='sr-only'>Help</span>
          </Button>
        </div>
      </div>
    </div>
  );
};
